import React, { useState, useEffect, useRef } from "react";
import { X, Check, Camera, Layers } from "lucide-react";
import shopGroupApi from "../../../../api/shopGroupApi";
import otherApi from "../../../../api/otherApi";
import { useNotification } from "../../../../hooks/useNotification";

export default function ShopGroupModal({
  isOpen,
  closeModal,
  shops,
  staffId,
  editGroup,
  onSaved,
}) {
  const { addNotification } = useNotification();
  const [name, setName] = useState("");
  const [selectedShopIds, setSelectedShopIds] = useState([]);
  const [defaultSearchShopId, setDefaultSearchShopId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editGroup) {
      setName(editGroup.name || "");
      setSelectedShopIds(editGroup.shopIds || []);
      setDefaultSearchShopId(editGroup.defaultSearchShopId || "");
      setAvatar(editGroup.avatar || null);
      setAvatarFile(null);
    } else {
      setName("");
      setSelectedShopIds([]);
      setDefaultSearchShopId("");
      setAvatar(null);
      setAvatarFile(null);
    }
  }, [editGroup, isOpen]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        addNotification("Vui lòng chọn file ảnh", -1);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        addNotification("Kích thước ảnh tối đa 5MB", -1);
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setAvatar(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleShopToggle = (shopId) => {
    setSelectedShopIds((prev) => {
      if (prev.includes(shopId)) {
        const newIds = prev.filter((id) => id !== shopId);
        if (defaultSearchShopId === shopId) {
          setDefaultSearchShopId(newIds[0] || "");
        }
        return newIds;
      } else {
        if (prev.length === 0) {
          setDefaultSearchShopId(shopId);
        }
        return [...prev, shopId];
      }
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      addNotification("Vui lòng nhập tên nhóm", -1);
      return;
    }
    if (selectedShopIds.length === 0) {
      addNotification("Vui lòng chọn ít nhất 1 Zalo", -1);
      return;
    }

    setIsLoading(true);
    try {
      let avatarUrl = avatar;
      if (avatarFile) {
        try {
          const uploadRes = await otherApi.postImage(
            `shop_group_${Date.now()}_${avatarFile.name}`,
            avatarFile,
          );
          avatarUrl = uploadRes.url;
        } catch (uploadError) {
          console.error("Avatar upload failed:", uploadError);
          addNotification("Không thể upload avatar", -1);
          setIsLoading(false);
          return;
        }
      }

      const data = {
        staffId,
        name: name.trim(),
        shopIds: selectedShopIds,
        defaultSearchShopId: defaultSearchShopId || selectedShopIds[0],
        avatar: avatarUrl,
      };

      let res;
      if (editGroup) {
        res = await shopGroupApi.update(editGroup.id, data);
      } else {
        res = await shopGroupApi.create(data);
      }

      if (res.status === 1) {
        addNotification(res.message, 1);
        onSaved && onSaved();
        closeModal();
      } else {
        addNotification(res.message, -1);
      }
    } catch (error) {
      addNotification("Có lỗi xảy ra", -1);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full relative max-h-[90vh] overflow-hidden flex flex-col">
        <button
          onClick={closeModal}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors z-10"
          aria-label="Đóng"
        >
          <X size={20} />
        </button>

        <div className="p-5 flex-1 overflow-auto">
          <h2 className="text-lg font-semibold text-center text-gray-900 mb-4">
            {editGroup ? "Sửa nhóm Zalo" : "Tạo nhóm Zalo"}
          </h2>

          {/* Avatar upload */}
          <div className="flex justify-center mb-2">
            <div
              className="relative cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center overflow-hidden">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Group avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Layers size={24} className="text-white" />
                )}
              </div>
              <div className="absolute inset-0 rounded-full bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={18} className="text-white" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
          </div>
          <p className="text-center text-[11px] text-gray-500 mb-4">
            Nhấn để chọn avatar nhóm
          </p>

          {/* Tên nhóm */}
          <div className="mb-3">
            <label className="block text-[12px] font-medium text-gray-700 mb-1">
              Tên nhóm
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-1.5 text-[13px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-gray-900 bg-white"
              placeholder="Nhập tên nhóm"
            />
          </div>

          {/* Chọn Zalo vào nhóm */}
          <div className="mb-3">
            <label className="block text-[12px] font-medium text-gray-700 mb-1">
              Chọn Zalo vào nhóm
              {selectedShopIds.length > 0 && (
                <span className="ml-1.5 text-[11px] text-blue-600 font-semibold">
                  ({selectedShopIds.length})
                </span>
              )}
            </label>
            <div className="border border-gray-300 rounded-md max-h-56 overflow-auto">
              {shops.length === 0 ? (
                <div className="p-3 text-center text-[12px] text-gray-500">
                  Chưa có Zalo nào
                </div>
              ) : (
                shops.map((shop) => (
                  <label
                    key={shop._id}
                    className="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedShopIds.includes(shop._id)}
                      onChange={() => handleShopToggle(shop._id)}
                      className="mr-2.5 h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <img
                      src={shop.avatar}
                      alt=""
                      className="h-6 w-6 rounded-full mr-2 object-cover"
                    />
                    <span className="text-[13px] text-gray-700 flex-1 truncate">
                      {shop.name}
                    </span>
                    {selectedShopIds.includes(shop._id) && (
                      <Check className="ml-auto h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                    )}
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Zalo tìm kiếm SĐT */}
          <div className="mb-5">
            <label className="block text-[12px] font-medium text-gray-700 mb-1">
              Zalo tìm kiếm SĐT
            </label>
            <select
              value={defaultSearchShopId}
              onChange={(e) => setDefaultSearchShopId(e.target.value)}
              className="w-full px-3 py-1.5 text-[13px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-gray-900 bg-white"
              disabled={selectedShopIds.length === 0}
            >
              <option value="">Chọn Zalo mặc định</option>
              {shops
                .filter((shop) => selectedShopIds.includes(shop._id))
                .map((shop) => (
                  <option key={shop._id} value={shop._id}>
                    {shop.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Nút lưu */}
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 text-[13px] rounded-md font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? "Đang lưu..." : editGroup ? "Cập nhật nhóm" : "Lưu nhóm"}
          </button>
        </div>
      </div>
    </div>
  );
}
